/**
 * Critique Scroll Utilities: Critique View URL State Management
 * Feature: 040-critique-url-state
 *
 * Utilities for scrolling to specific critiques in the detail panel.
 * Uses native scrollIntoView API for smooth, hardware-accelerated animations.
 */

import { useEffect } from 'react';

/**
 * Scroll to a specific critique in the critique panel
 *
 * Uses native scrollIntoView with smooth animation.
 * Critiques must have `data-critique-id` attribute for targeting.
 *
 * @param critiqueId - Global ID of the critique to scroll to
 * @param behavior - Scroll animation ('smooth' for user clicks, 'auto' for instant)
 *
 * @example
 * ```tsx
 * // Smooth scroll when user clicks critique in feed
 * scrollToCritique('cm9a', 'smooth');
 *
 * // Instant scroll on initial page load
 * scrollToCritique('cm9a', 'auto');
 * ```
 *
 * Performance: Typically 300-400ms for smooth scroll (well under 500ms requirement)
 */
export function scrollToCritique(critiqueId: string, behavior: 'smooth' | 'auto' = 'smooth'): void {
  const element = document.querySelector(`[data-critique-id="${critiqueId}"]`);

  if (element) {
    element.scrollIntoView({
      behavior,
      block: 'center', // Center critique in viewport for best UX
      inline: 'nearest',
    });
  }
}

/**
 * Hook to automatically scroll when comment ID changes
 *
 * Automatically scrolls to critique when URL comment parameter updates.
 * Includes small delay to ensure DOM is ready after photo selection.
 *
 * @param commentId - Global ID of the comment to scroll to (null to skip)
 *
 * @example
 * ```tsx
 * function CollectionCritiqueModePage() {
 *   const { state } = useCritiqueUrlState(collectionId, photos);
 *
 *   // Automatically scroll when comment ID in URL changes
 *   useScrollToCritique(state.commentId);
 *
 *   return <CritiqueComments selectedCritiqueId={state.commentId} />;
 * }
 * ```
 */
export function useScrollToCritique(commentId: string | null): void {
  useEffect(() => {
    if (commentId) {
      // Small delay to ensure DOM is ready after photo selection
      const timeoutId = setTimeout(() => {
        scrollToCritique(commentId);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [commentId]);
}
